import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  MatchEventPublic,
  MatchPublic,
  TeamLandmark,
  TeamPublic,
  TeamTheme,
} from '@mondy/shared';
import {
  canPredictEarly,
  getTeamLandmark,
  getTeamTheme,
  isMatchLive,
  isSvgLandmark,
  liveMinuteLabel,
  matchPausedLabel,
  predictionWindowState,
} from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { useNavHistory } from '@/state/navHistory';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  STAGE_LABELS,
  formatKickoff,
  formatKickoffMobileDetail,
  formatKickoffShort,
  matchLabel,
} from '@/lib/format';
import { EarlyPredictionUpgradeCTA } from '@/components/EarlyPredictionUpgradeCTA';
import {
  buildGroups,
  GroupCard,
  ThirdPlaceLegend,
} from '@/components/GroupStandings';
import { FormationPitch } from '@/components/FormationPitch';
import { LockBadge } from '@/components/LockBadge';
import { useBracketProjection } from '@/hooks/useBracketProjection';
import { PredictionForm } from '@/components/PredictionForm';
import { MatchPredictions } from '@/components/MatchPredictions';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';

interface MatchResponse {
  match: MatchPublic;
}

interface TeamsResponse {
  teams: TeamPublic[];
}

interface MatchListResponse {
  matches: MatchPublic[];
}

function countdown(kickoffMs: number): string {
  const delta = kickoffMs - Date.now();
  if (delta <= 0) return 'now';
  const h = Math.floor(delta / 3_600_000);
  const m = Math.floor((delta % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

// Perceived brightness (ITU-R BT.601 luma) of a #RRGGBB color, 0–255.
function kitBrightness(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function hexToRgb(hex: string): [number, number, number] {
  const v = hex.replace('#', '');
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  ];
}

function scaleBrightness(hex: string, factor: number): string {
  const channels = hexToRgb(hex).map((c) =>
    Math.round(Math.min(255, Math.max(0, c * factor))),
  );
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

// Push the two kit colors apart in tone so adjacent swatches read as distinct:
// the brighter color gets +0% brightness, the darker one −0% (spread disabled).
function spreadKitColors(c1: string, c2: string): [string, string] {
  if (kitBrightness(c1) >= kitBrightness(c2)) {
    return [scaleBrightness(c1, 1.0), scaleBrightness(c2, 1.0)];
  }
  return [scaleBrightness(c1, 1.0), scaleBrightness(c2, 1.0)];
}

export function MatchDetailPage() {
  const { goBack } = useNavHistory();
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const query = useQuery({
    queryKey: ['match', id],
    queryFn: () => api.get<MatchResponse>(`/api/matches/${id}`),
    enabled: Boolean(id),
    // Poll while live/paused so the header score and the per-stage
    // provisional points in PredictionForm track the running result.
    refetchInterval: (q) =>
      q.state.data && isMatchLive(q.state.data.match) ? 30 * 1000 : false,
  });

  // Group-standings feeders. Reuse the exact query keys/staleTimes from the
  // Teams and Schedule pages so TanStack Query serves these from cache when the
  // user navigated in from there — no extra fetch. Gated to group-stage matches
  // so knockout matches fire neither request.
  const isGroupStage = query.data?.match.stage === 'group';
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
    staleTime: 60 * 60 * 1000,
    enabled: isGroupStage,
  });

  const groupMatchesQuery = useQuery({
    queryKey: ['matches', { stage: 'group' }],
    queryFn: () => api.get<MatchListResponse>('/api/matches?stage=group'),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    enabled: isGroupStage,
  });

  const groups = useMemo(
    () =>
      teamsQuery.data
        ? buildGroups(teamsQuery.data.teams, groupMatchesQuery.data?.matches ?? [])
        : [],
    [teamsQuery.data, groupMatchesQuery.data],
  );

  // A knockout match still on placeholders shows its PROJECTED entrants + ✓/?
  // certainty, resolved from live standings like the bracket. Disabled for group
  // matches (and resolved knockout matches), so it never double-fetches with the
  // group-standings path above — they're never both active for one match.
  const projMatch = query.data?.match;
  const needsProjection = !!projMatch && projMatch.stage !== 'group' && !projMatch.team1;
  const { resolved, lockedIds } = useBracketProjection(needsProjection);

  const [tab, setTab] = useState<'predictions' | 'lineups'>('predictions');
  const [, force] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => force((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  if (query.isLoading) {
    return <Spinner fullScreen label="Loading match" />;
  }
  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div style={{ textAlign: 'center', padding: '64px 20px' }}>
        <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          {notFound ? 'Match not found' : 'Something went wrong'}
        </p>
        <Link
          to="/schedule"
          className="btn btn-ghost"
          style={{ display: 'inline-flex' }}
        >
          Back to schedule
        </Link>
      </div>
    );
  }

  const m = query.data!.match;
  const { t1, t2 } = matchLabel(m);
  // Projected knockout identity for display only (flag, code, name, badge). All
  // result/prediction logic below stays on m.team1/m.team2.
  const proj = needsProjection ? resolved?.get(m.id) : undefined;
  const team1 = proj?.team1 ?? m.team1;
  const team2 = proj?.team2 ?? m.team2;
  const lock1 = needsProjection && lockedIds && team1 ? lockedIds.has(team1.id) : null;
  const lock2 = needsProjection && lockedIds && team2 ? lockedIds.has(team2.id) : null;
  const groupStandings = m.group
    ? groups.find((g) => g.group === m.group)
    : undefined;
  const stageLabel = m.group ? `Group ${m.group}` : STAGE_LABELS[m.stage];
  const hasResult = m.result != null;
  const kickoffMs = new Date(m.kickoffUtc).getTime();
  const isUpcoming = m.status === 'scheduled' && kickoffMs > Date.now();
  // For upcoming scheduled matches, decide whether the third tile shows
  // "Opens in …" (outside 24h window) vs "Locks in …" (inside window).
  const teamsResolved = m.team1 !== null && m.team2 !== null;
  const allowEarlyPrediction =
    !!user && canPredictEarly(user.paymentTier) && teamsResolved;
  const windowState = isUpcoming
    ? predictionWindowState(m, { allowEarlyPrediction })
    : null;
  const isTooEarly = windowState?.state === 'too_early';
  const opensAtMs = isTooEarly ? windowState.opensAtMs : null;

  // While the match is live, collapse ET/PEN into the main score by picking
  // the most-progressed stage available. When finished, extra time takes over
  // as the main score when present (else full time), and FT/PEN render as
  // secondary labels below.
  const live = isMatchLive(m);
  const mainScore =
    live && m.result
      ? m.result.penalties ?? m.result.extraTime ?? m.result.fullTime
      : m.result?.extraTime ?? m.result?.fullTime ?? null;
  const showSecondaryStages =
    m.status === 'finished' &&
    m.result != null &&
    (m.result.extraTime != null || m.result.penalties != null);
  // Kit-color swatches sit beneath the score while live or finished, and
  // below the "VS" text before kick-off.
  const showKitColors =
    (live || isUpcoming || m.status === 'finished') &&
    (!!m.team1?.kitColor || !!m.team2?.kitColor);
  // When both kits are present, spread them apart in tone so the two swatches
  // sitting side-by-side don't blur together; falls back to the raw color
  // whenever only one team has a kit color.
  const [kit1, kit2] =
    m.team1?.kitColor && m.team2?.kitColor
      ? spreadKitColors(m.team1.kitColor, m.team2.kitColor)
      : [m.team1?.kitColor ?? null, m.team2?.kitColor ?? null];

  return (
    <>
      <div className="page-head match-detail-head">
        <div className="page-title">
          <div className="k">
            <button
              type="button"
              onClick={() => goBack('/schedule')}
              style={{
                color: 'inherit',
                textDecoration: 'none',
                marginRight: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                padding: 0,
              }}
            >
              ←
            </button>
            Match {String(m.matchNumber).padStart(2, '0')} · {stageLabel}
          </div>
          <h1 style={{ fontSize: 38 }}>
            {team1?.name ?? t1} <span className="emph">vs</span> {team2?.name ?? t2}
          </h1>
        </div>
        <button type="button" onClick={() => goBack('/schedule')} className="btn btn-ghost">
          Back
        </button>
      </div>

      <div className="md-card">
        <div className="md-mobile-fixture-meta">
          <div className="date">{formatKickoffMobileDetail(m.kickoffUtc)}</div>
          <div className="venue">
            {stageLabel} ·{' '}
            {m.stadium ? (
              <Link
                to={`/stadiums/${m.stadium.id}`}
                style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}
              >
                {m.stadium.name}
              </Link>
            ) : (
              m.venue ?? 'TBA'
            )}
          </div>
        </div>

        <div className="md-scoreline">
          <div className="md-team">
            {team1 ? (
              <LandmarkHalo
                side="left"
                theme={getTeamTheme(team1.fifaCode)}
                landmark={getTeamLandmark(team1.fifaCode)}
              />
            ) : null}
            <BigTeamFlag
              fifaCode={team1?.fifaCode ?? null}
              fallbackFlagUrl={team1?.flagUrl ?? null}
              teamId={team1?.id ?? null}
            />
            <div className="code">
              {team1?.fifaCode ?? '—'}
              {lock1 !== null ? <LockBadge locked={lock1} /> : null}
            </div>
            <div className="name">{team1?.name ?? t1}</div>
          </div>
          <div className={`md-vs ${!hasResult ? 'upcoming' : ''}`}>
            <div className="md-vs-score">
              {mainScore ? (
                <>
                  <span
                    className="tabular"
                    style={live ? { color: 'var(--hot)' } : undefined}
                  >
                    {mainScore.team1}
                  </span>
                  <span className="dash">–</span>
                  <span
                    className="tabular"
                    style={live ? { color: 'var(--hot)' } : undefined}
                  >
                    {mainScore.team2}
                  </span>
                </>
              ) : (
                <span>VS</span>
              )}
            </div>
            {showKitColors ? (
              <div className="md-kit-colors">
                {kit1 ? (
                  <span
                    className="md-kit-swatch"
                    style={{ background: kit1 }}
                    aria-label={`${t1} kit color`}
                  />
                ) : null}
                {kit2 ? (
                  <span
                    className="md-kit-swatch"
                    style={{ background: kit2 }}
                    aria-label={`${t2} kit color`}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="md-team">
            {team2 ? (
              <LandmarkHalo
                side="right"
                theme={getTeamTheme(team2.fifaCode)}
                landmark={getTeamLandmark(team2.fifaCode)}
              />
            ) : null}
            <BigTeamFlag
              fifaCode={team2?.fifaCode ?? null}
              fallbackFlagUrl={team2?.flagUrl ?? null}
              teamId={team2?.id ?? null}
            />
            <div className="code">
              {team2?.fifaCode ?? '—'}
              {lock2 !== null ? <LockBadge locked={lock2} /> : null}
            </div>
            <div className="name">{team2?.name ?? t2}</div>
          </div>
        </div>

        {showSecondaryStages ? (
          <div style={{ textAlign: 'center' }}>
            {m.result!.extraTime ? (
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  color: 'var(--fg-muted)',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  marginRight: 12,
                }}
              >
                FT {m.result!.fullTime.team1}–{m.result!.fullTime.team2}
              </span>
            ) : null}
            {m.result!.penalties ? (
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  color: 'var(--fg-muted)',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                }}
              >
                Pens {m.result!.penalties.team1}–{m.result!.penalties.team2}
              </span>
            ) : null}
          </div>
        ) : null}

        {isUpcoming ? (
          <div style={{ textAlign: 'center' }}>
            <span className="vs-label" style={{ color: 'var(--hot)' }}>
              in {countdown(kickoffMs)}
            </span>
          </div>
        ) : null}

        <MatchTimeline events={m.events} />

        <div className="md-kick">
          <div className="k">
            <div className="lbl">Kick-off</div>
            <div className="val">{formatKickoffShort(m.kickoffUtc)}</div>
            <div className="sub">{formatKickoff(m.kickoffUtc)}</div>
          </div>
          <div className="k">
            <div className="lbl">Venue</div>
            <div className="val">
              {m.stadium ? (
                <Link
                  to={`/stadiums/${m.stadium.id}`}
                  style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}
                >
                  {m.stadium.name}
                </Link>
              ) : (
                m.venue ?? 'TBA'
              )}
            </div>
            <div className="sub">
              {m.stadium
                ? [m.stadium.city, m.stadium.country].filter(Boolean).join(', ')
                : m.city ?? ''}
            </div>
          </div>
          <div className="k">
            <div className="lbl">
              {isUpcoming ? (isTooEarly ? 'Opens in' : 'Locks in') : 'Status'}
            </div>
            <div
              className="val"
              style={{
                color: live ? 'var(--hot)' : 'var(--fg)',
                fontSize:
                  m.status === 'paused' && (m.minute ?? 0) >= 120
                    ? 16
                    : undefined,
              }}
            >
              {isUpcoming
                ? countdown(isTooEarly && opensAtMs !== null ? opensAtMs : kickoffMs)
                : m.status === 'paused'
                  ? matchPausedLabel(m.minute)
                  : live
                    ? `LIVE${typeof m.minute === 'number' ? ` · ${liveMinuteLabel(m.minute, m.addedTime)}` : ''}`
                    : m.status === 'finished'
                      ? 'Full time'
                      : 'Pending'}
            </div>
            <div className="sub">
              {isUpcoming
                ? isTooEarly
                  ? 'Predictions open 24h before kick-off.'
                  : 'Predictions lock at kick-off'
                : ''}
            </div>
          </div>
        </div>
      </div>

      {m.stage === 'group' && groupStandings && groupMatchesQuery.data ? (
        <section className="mt-6 mb-8 flex flex-col gap-3">
          <GroupCard {...groupStandings} />
          {groupStandings.rows[2]?.thirdPlaceQualifying ? (
            <ThirdPlaceLegend />
          ) : null}
        </section>
      ) : null}

      <div
        role="tablist"
        aria-label="Match sections"
        style={{
          display: 'inline-flex',
          gap: 4,
          padding: 4,
          margin: '20px 0 4px',
          background: 'var(--bg-2)',
          border: '1px solid var(--line)',
          borderRadius: 999,
        }}
      >
        <TabButton active={tab === 'predictions'} onClick={() => setTab('predictions')}>
          Predictions
        </TabButton>
        <TabButton active={tab === 'lineups'} onClick={() => setTab('lineups')}>
          Line-ups
        </TabButton>
      </div>

      {tab === 'predictions' ? (
        <>
          <div className="section-head md-prediction-head">
            <h2>
              <span className="bar" />
              Your prediction
            </h2>
          </div>
          <EarlyPredictionUpgradeCTA />
          <PredictionForm match={m} />
          <MatchPredictions match={m} />
        </>
      ) : (
        <FormationPitch
          lineups={m.lineups ?? { team1: null, team2: null }}
          homeLabel={t1}
          awayLabel={t2}
          homeColor={getTeamTheme(m.team1?.fifaCode ?? null)?.palette.primary}
          awayColor={getTeamTheme(m.team2?.fifaCode ?? null)?.palette.primary}
        />
      )}
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        border: 'none',
        cursor: 'pointer',
        padding: '8px 18px',
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        background: active ? 'var(--hot)' : 'transparent',
        color: active ? 'var(--bg)' : 'var(--fg-muted)',
        transition: 'background 0.15s, color 0.15s',
      }}
    >
      {children}
    </button>
  );
}

/** Soccer-ball glyph for goals/penalties. `og` tints it red for own goals. */
function BallIcon({ og = false }: { og?: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke={og ? 'var(--hot, #e5484d)' : 'currentColor'}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.2l3.4 2.5-1.3 4h-4.2l-1.3-4L12 7.2z" />
      <path d="M12 4v3.2M19.6 9.9l-3.5 1.3M16.8 17.1l-2.7-3M9.9 14.1l-2.7 3M4.4 9.9l3.5 1.3" />
    </svg>
  );
}

/** Solid red rectangle for red cards. */
function RedCardIcon() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" aria-hidden>
      <rect
        x="1"
        y="1"
        width="9"
        height="11"
        rx="1.5"
        fill="var(--hot, #e5484d)"
      />
    </svg>
  );
}

/** Solid yellow rectangle for yellow cards. */
function YellowCardIcon() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" aria-hidden>
      <rect
        x="1"
        y="1"
        width="9"
        height="11"
        rx="1.5"
        fill="var(--sun, #f5c518)"
      />
    </svg>
  );
}

/** Soccer ball marked "P" for a goal scored from a penalty. */
function PenaltyIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <text
        x="12"
        y="16.6"
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fill="currentColor"
        stroke="none"
      >
        P
      </text>
    </svg>
  );
}

/** Struck-through ball for a missed penalty. */
function MissedPenaltyIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 18.4L18.4 5.6" />
    </svg>
  );
}

/** Two opposing arrows for substitutions: green in (up), red out (down). */
function SubIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8 20V6M8 6l-3 3M8 6l3 3" stroke="var(--ok, #46a758)" />
      <path d="M16 4v14M16 18l-3-3M16 18l3-3" stroke="var(--hot, #e5484d)" />
    </svg>
  );
}

const TIMELINE_EVENT_TYPES = new Set([
  'goal',
  'penalty',
  'missed_penalty',
  'own_goal',
  'red_card',
  'yellow_card',
  'substitution',
]);

/** Icon + minute + body for a single event, used in whichever grid column
 * matches the event's side. Open-play goals show a ball + scorer (+assist),
 * penalty goals a ball marked "P", missed penalties a struck-through ball,
 * own goals a red-tinted ball + "(OG)", red cards a red rectangle, yellow
 * cards a yellow rectangle, substitutions a pair of arrows (in bold, out
 * muted). */
function EventContent({ e }: { e: MatchEventPublic }) {
  if (e.type === 'substitution') {
    return (
      <div className="md-event">
        <span className="md-event-icon">
          <SubIcon />
        </span>
        <span className="md-event-min tabular">{e.minute}&apos;</span>
        {e.assist || e.player ? (
          <span className="md-event-body">
            {e.assist ? (
              <span className="md-event-sub-in">{e.assist}</span>
            ) : null}
            {e.player ? (
              <span className="md-event-sub-out">{e.player}</span>
            ) : null}
          </span>
        ) : null}
      </div>
    );
  }
  const isGoalish =
    e.type === 'goal' || e.type === 'penalty' || e.type === 'own_goal';
  return (
    <div className="md-event">
      <span className="md-event-icon">
        {e.type === 'red_card' ? (
          <RedCardIcon />
        ) : e.type === 'yellow_card' ? (
          <YellowCardIcon />
        ) : e.type === 'penalty' ? (
          <PenaltyIcon />
        ) : e.type === 'missed_penalty' ? (
          <MissedPenaltyIcon />
        ) : (
          <BallIcon og={e.type === 'own_goal'} />
        )}
      </span>
      <span className="md-event-min tabular">{e.minute}&apos;</span>
      {e.player ? (
        <span className="md-event-body">
          <span className="md-event-player">
            {e.player}
            {e.type === 'own_goal' ? ' (OG)' : ''}
          </span>
          {isGoalish && e.assist ? (
            <span className="md-event-assist">assist: {e.assist}</span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Single combined, row-synchronized event timeline for both teams. Events
 * arrive ascending and interleaved across both sides; we keep arrival order
 * (which preserves stoppage-time ties) and emit two grid cells per event —
 * the content cell in that event's team column and an empty cell in the other
 * column — so vertical position encodes time across BOTH teams. team1 (home)
 * sits left/left-aligned, team2 (away) mirrors to the right.
 */
function MatchTimeline({ events }: { events: MatchEventPublic[] }) {
  // events arrive ascending; reverse the fresh filtered array for descending
  // (newest-first) order while preserving stoppage-time tie order.
  const shown = events.filter((e) => TIMELINE_EVENT_TYPES.has(e.type)).reverse();
  if (shown.length === 0) return null;

  // Events are descending (minute non-increasing), so the half-time break sits
  // before the first event with minute <= 45. Only show it when a later-half
  // event precedes it — i.e. there are events on both sides of the break.
  const halftimeIndex = shown.findIndex((e) => e.minute <= 45);
  const showHalftime = halftimeIndex > 0;

  return (
    <div className="md-timeline">
      {shown.map((e, i) => (
        <Fragment key={i}>
          {showHalftime && i === halftimeIndex ? (
            <div className="md-timeline-half">Half-time</div>
          ) : null}
          <div className="md-timeline-cell">
            {e.side === 'team1' ? <EventContent e={e} /> : null}
          </div>
          <div className="md-timeline-cell md-timeline-right">
            {e.side === 'team2' ? <EventContent e={e} /> : null}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Large national-flag tile for the match-detail scoreline. We render the
 * real flagcdn image rather than a regional-indicator emoji — those render
 * as bare two-letter codes (e.g. "US", "PY") on Windows/Chrome.
 */
function BigTeamFlag({
  fifaCode,
  fallbackFlagUrl,
  teamId,
}: {
  fifaCode: string | null;
  fallbackFlagUrl: string | null;
  teamId: string | null;
}) {
  const flag = (
    <TeamFlag
      code={fifaCode}
      url={fallbackFlagUrl}
      size="md"
      className="md-flag-tile"
    />
  );
  if (!teamId) return flag;
  // `display: contents` keeps the flag tile a direct flex child of
  // `.md-team` so the mobile `order`/halo rules still target it; cursor
  // inherits to the span so the whole tile reads as clickable.
  return (
    <Link
      to={`/teams/${teamId}`}
      aria-label={`View ${fifaCode ?? 'team'} squad`}
      style={{ display: 'contents', cursor: 'pointer' }}
    >
      {flag}
    </Link>
  );
}

/**
 * Landmark halo anchored inside each team block, behind that team's flag
 * and name. This matches the reference layout where the landmark is paired
 * with the country, not centered behind the VS block.
 */
function LandmarkHalo({
  side,
  theme,
  landmark,
}: {
  side: 'left' | 'right';
  theme: TeamTheme | null;
  landmark: TeamLandmark;
}) {
  const color = theme?.palette.primary ?? 'var(--hot)';
  const isSvg = isSvgLandmark(landmark.emoji);

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: 8,
        [side]: '4%',
        transform: side === 'left' ? 'translateX(-14%)' : 'translateX(14%)',
        width: 230,
        height: 230,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 0,
      }}
    >
      {/* Color halo — radial in the team primary color */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color} 0%, ${color}00 65%)`,
          opacity: 0.34,
          filter: 'blur(5px)',
        }}
      />
      {/* Landmark (desaturated, luminosity-blended) */}
      <div
        style={{
          position: 'relative',
          fontSize: 142,
          lineHeight: 1,
          opacity: 0.48,
          filter: 'grayscale(1) contrast(1.08) brightness(1.04)',
          mixBlendMode: 'luminosity',
          width: isSvg ? 142 : 'auto',
          height: isSvg ? 142 : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isSvg ? (
          <div
            style={{ width: 142, height: 142 }}
            dangerouslySetInnerHTML={{ __html: landmark.emoji }}
          />
        ) : (
          landmark.emoji
        )}
      </div>
    </div>
  );
}
